import { apolloClient } from '@/apollo';
import { NotificationSettings, NotificationsManagerImplementation } from '.';
import { GET_USER_ACCOUNT_ALERTS, UPDATE_USER_ACCOUNT_ALERTS_NESTED } from '../graphql';
import {
	GetUserAccountAlertsQuery,
	GetUserAccountAlertsQueryVariables,
	UpdateUserAccountAlertsNestedMutation,
	UpdateUserAccountAlertsNestedMutationVariables,
} from '../graphql.generated';

export class WebNotificationsManager implements NotificationsManagerImplementation {
	readonly notificationsEnabled = async ({ id }: { id: string }) => {
		const { data } = await apolloClient.query<
			GetUserAccountAlertsQuery,
			GetUserAccountAlertsQueryVariables
		>({
			query: GET_USER_ACCOUNT_ALERTS,
			variables: { id },
		});

		return data?.user?.binReminderEnabled ?? false;
	};

	readonly listNotifications = async ({ id }: { id: string }) => {
		const { data } = await apolloClient.query<
			GetUserAccountAlertsQuery,
			GetUserAccountAlertsQueryVariables
		>({
			query: GET_USER_ACCOUNT_ALERTS,
			variables: { id },
			fetchPolicy: 'network-only',
		});

		return data?.user?.userAlerts ?? [];
	};

	readonly notificationSettingsValuesChanged = async (
		{ id }: { id: string },
		notificationSettings: NotificationSettings
	) => {
		const { enabled: binReminderEnabled, ...rest } = notificationSettings;

		await apolloClient.mutate<
			UpdateUserAccountAlertsNestedMutation,
			UpdateUserAccountAlertsNestedMutationVariables
		>({
			mutation: UPDATE_USER_ACCOUNT_ALERTS_NESTED,
			variables: {
				alertInput: {
					...rest,
					user: { id },
				},
				userInput: {
					id,
					binReminderEnabled,
				},
			},
			update: (cache, { data }) => {
				// Update the UserAlert in cache with new values
				if (data?.alerts?.[0]) {
					const updatedAlert = data.alerts[0];
					if (updatedAlert) {
						const cacheId = `UserAlert:${updatedAlert.id}`;

						cache.modify({
							id: cacheId,
							fields: {
								// Update all the fields from the rest variable
								...Object.keys(rest).reduce(
									(acc, key) => {
										acc[key] = () => rest[key as keyof typeof rest];
										return acc;
									},
									{} as Record<string, () => unknown>
								),
							},
						});
					}
				}
			},
		});

		return notificationSettings;
	};

	readonly notificationSettings = async ({ id }: { id: string }) => {
		const { data } = await apolloClient.query<
			GetUserAccountAlertsQuery,
			GetUserAccountAlertsQueryVariables
		>({
			query: GET_USER_ACCOUNT_ALERTS,
			variables: { id },
		});

		if (!data?.user?.userAlerts?.[0]) return null;
		const alert = data.user.userAlerts[0];

		return {
			...alert,
			enabled: data.user.binReminderEnabled,
		};
	};
}
